import React from "react";
import {
  ExpandedTicker,
  ExpandedTickerSection,
  Image,
  SanityLink,
  Ticker,
} from "@bared/ui";
import { Swiper, SwiperSlide } from "swiper/react";
import { ComponentProps, GatsbyCustomImage } from "@ts/components";
import clsx from "clsx";
import { InternalType, LinkType } from "@bared/groq";

import { useLiveChat } from "~/hooks/useLiveChat";

type TickerContentProps = {
  _key: string;
  title: string;
  image: GatsbyCustomImage;
  textLink: LinkType[] | InternalType[];
  liveChat: boolean;
  imageReverse: boolean;
  secondaryColor: "Charcoal" | "Dark Beige";
};

type TickerProps = ComponentProps &
  Pick<TickerContentProps, "secondaryColor"> & {
    _type: string;
    items: TickerContentProps[];
    delay: number;
  };

const USPContent = ({
  liveChat,
  imageReverse,
  secondaryColor,
  image,
  textLink,
  title,
}: TickerContentProps) => {
  const link = textLink && textLink.length > 0 ? textLink[0] : null;

  const { open } = useLiveChat();

  if (liveChat)
    return (
      <button
        className={clsx(
          imageReverse && "flex-row-reverse",
          "flex flex-row items-center",
        )}
        onClick={() => open()}
        onKeyDown={() => open()}
      >
        {image ? (
          <Image
            data={image.asset}
            source="sanity"
            alt={image?.alt}
            className="h-midi w-mini"
          />
        ) : null}
        <span
          className={clsx(
            secondaryColor === "Charcoal" &&
              "text-secondary hover:text-primary",
            "text-primary hover:text-secondary",
            "px-micro text-hint",
          )}
        >
          {title}
        </span>
      </button>
    );
  if (link)
    return (
      <SanityLink
        className={clsx(
          imageReverse && "flex-row-reverse",
          "flex flex-row items-center",
        )}
        data={link}
        target="_blank"
        rel="noopener noreferrer"
      >
        {image ? (
          <Image
            data={image.asset}
            source="sanity"
            alt={image?.alt}
            className="h-midi w-mini"
          />
        ) : null}
        <span
          className={clsx(
            secondaryColor === "Charcoal" &&
              "text-secondary hover:text-primary",
            "text-primary hover:text-secondary",
            "px-micro text-hint",
          )}
        >
          {title}
        </span>
      </SanityLink>
    );
  return (
    <div
      className={clsx(
        imageReverse && "flex-row-reverse",
        "flex flex-row items-center",
      )}
    >
      {image ? (
        <Image
          data={image.asset}
          source="sanity"
          alt={image?.alt}
          className="h-midi w-mini"
        />
      ) : null}
      <span
        className={clsx(
          secondaryColor === "Charcoal" && "text-secondary",
          "text-primary",
          "px-micro text-hint",
        )}
      >
        {title}
      </span>
    </div>
  );
};

export const USPSwiper = ({ items, delay, secondaryColor }: TickerProps) => {
  return (
    <>
      <Ticker
        delay={delay}
        className="flex justify-center border-y border-tertiary py-midi md:hidden"
      >
        {items?.map(
          ({ _key, title, image, textLink, liveChat, imageReverse }) => {
            return (
              <div
                key={title}
                className="flex items-center space-x-micro text-center text-primary"
              >
                <USPContent
                  _key={_key}
                  title={title}
                  image={image}
                  textLink={textLink}
                  liveChat={liveChat}
                  imageReverse={imageReverse}
                  secondaryColor={secondaryColor}
                />
              </div>
            );
          },
        )}
      </Ticker>
      <Swiper
        loop={items.length > 3 ? true : false}
        autoplay={items.length > 3 ? true : false}
        slidesPerView={3}
        spaceBetween={10}
        className="hidden border-y border-tertiary py-mini md:block"
      >
        <ExpandedTicker>
          {items?.map(
            ({ _key, title, image, textLink, liveChat, imageReverse }) => {
              return (
                <SwiperSlide>
                  <ExpandedTickerSection
                    id={_key}
                    className="flex justify-center"
                  >
                    <USPContent
                      _key={_key}
                      title={title}
                      image={image}
                      textLink={textLink}
                      liveChat={liveChat}
                      imageReverse={imageReverse}
                      secondaryColor={secondaryColor}
                    />
                  </ExpandedTickerSection>
                </SwiperSlide>
              );
            },
          )}
        </ExpandedTicker>
      </Swiper>
    </>
  );
};

export default USPSwiper;
